// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";
import { StatCard, QuickLink } from "./uiHelpers";

export function SellerOverview({ user, seller, onGoTo, setActive }) {
  const { data: stats = null } = useQuery({
    queryKey: ["seller-overview", seller?.id],
    enabled: !!seller?.id,
    staleTime: 60_000,
    queryFn: async () => {
      // Product count is sourced live (COUNT(*) on products) rather than from the
      // denormalised `sellers.product_count` column. A DB trigger keeps the column
      // in sync, but the header and stat card below should agree even if the
      // useAuth cache is stale — so we read the authoritative count here.
      // Filter matches the trigger in 20260419000002_seller_counter_triggers.sql
      // and SellerDashboard.jsx:94-96 (`deleted_at IS NULL AND status = 'active'`).
      const [ordersRes, escrowRes, payoutsRes, productsRes] = await Promise.all([
        supabase
          .from("sub_orders")
          .select("id, status")
          .eq("seller_id", seller.id),
        supabase
          .from("escrow_ledger")
          .select("seller_payout, status")
          .eq("seller_id", seller.id)
          .in("status", ["released"]),
        supabase
          .from("payouts")
          .select("amount, status")
          .eq("seller_id", seller.id),
        supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .eq("seller_id", seller.id)
          .is("deleted_at", null)
          .eq("status", "active"),
      ]);
      const subs = ordersRes.data || [];
      const earned = (escrowRes.data || []).reduce((t, e) => t + (e.seller_payout || 0), 0);
      const pending = subs.filter((s) => ["confirmed", "preparing", "shipped", "delivered"].includes(s.status)).length;
      // The live `payout_status` enum is ('pending','processing','paid','failed','frozen')
      // — there is no 'completed'. Filtering on 'completed' alone (the old behaviour)
      // always matched zero rows, so the "Paid out" KPI was stuck at K0.00 even for
      // sellers with a full payout history. Accept both for forward-compat with
      // PayoutsPanel, which already reads both.
      const paid = (payoutsRes.data || []).filter((p) => ["paid", "completed"].includes(p.status)).reduce((t, p) => t + (p.amount || 0), 0);
      return { totalOrders: subs.length, pending, earned, paid, productCount: productsRes.count || 0 };
    },
  });

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card pad={0} style={{ overflow: "hidden" }}>
        <div
          style={{
            height: 120,
            background: seller?.cover_url ? `url(${seller.cover_url}) center/cover` : `linear-gradient(135deg, ${C.hunter}, ${C.hunterLt})`,
          }}
        />
        <div style={{ padding: "16px 22px 22px", display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 18,
              marginTop: -50,
              background: seller?.logo_url ? `url(${seller.logo_url}) center/cover` : C.hunter,
              color: C.dust,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 900,
              border: `4px solid ${C.white}`,
              flexShrink: 0,
            }}
          >
            {!seller?.logo_url && (seller?.store_name?.[0] || "S").toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.charcoal }}>{seller?.store_name}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              ⭐ {(seller?.rating_avg || 0).toFixed(1)} ({seller?.rating_count || 0})
              {/* Live counts win; denormalised values on `seller` are a fallback for the
                  ~1 query tick before stats resolves so the header doesn't flicker to 0. */}
              &nbsp;·&nbsp; {stats?.productCount ?? seller?.product_count ?? 0} products
              &nbsp;·&nbsp; {stats?.totalOrders ?? seller?.total_orders ?? 0} total orders
            </div>
            {seller?.description && (
              <div style={{ fontSize: 12.5, color: C.slate, marginTop: 6, lineHeight: 1.5 }}>{seller.description}</div>
            )}
          </div>
          <Btn size="sm" kind="outline" onClick={() => onGoTo("store")}>Edit store</Btn>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatCard label="Total orders" value={stats ? stats.totalOrders : "—"} icon="package" />
        <StatCard label="In progress" value={stats ? stats.pending : "—"} icon="truck" />
        <StatCard label="Lifetime earned" value={stats ? fmtK(stats.earned) : "—"} icon="trending-up" />
        <StatCard label="Paid out" value={stats ? fmtK(stats.paid) : "—"} icon="banknote" />
      </div>

      <Card pad={22}>
        <Eyebrow>Quick actions</Eyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 10 }}>
          <QuickLink icon="tag" label="My products" onClick={() => setActive?.("products")} />
          <QuickLink icon="package" label="Orders" onClick={() => setActive?.("sorders")} />
          <QuickLink icon="bar-chart-3" label="Analytics" onClick={() => setActive?.("analytics")} />
          <QuickLink icon="banknote" label="Payouts" onClick={() => onGoTo("payouts")} />
          <QuickLink icon="star" label="Reviews" onClick={() => onGoTo("reviews")} />
          <QuickLink icon="tag" label="Promotions" onClick={() => onGoTo("promotions")} />
        </div>
      </Card>
    </div>
  );
}
